import { useState } from 'react';
import { Typography, InputNumber, message } from 'antd';
import { SaveOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import SettingsInfoButton from './SettingsInfoButton';

export default function TimeoutSection() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness, updateSelectedBusiness } = useBusinessStore();
  if (!selectedBusiness) return null;

  const [timeout, setTimeout] = useState(selectedBusiness.manualConfirmTimeoutMinutes);
  const [saving, setSaving] = useState(false);

  const hasChanges = timeout !== selectedBusiness.manualConfirmTimeoutMinutes;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await businessApi.updateTenant(selectedBusiness.id, {
        manualConfirmTimeoutMinutes: timeout,
      });
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('common:status.saved'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('common:errors.saveFailed'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#7c3aed' }}>
            <ClockCircleOutlined />
          </div>
          <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
            {t('owner:settings.timeout.title')}
          </Typography.Text>
        </div>
        <SettingsInfoButton
          title={t('owner:settings.timeout.info.title')}
          body={t('owner:settings.timeout.info.body')}
        />
      </div>

      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #6b7280)' }}>
          {t('owner:settings.timeout.hint')}
        </Typography.Text>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <InputNumber
            min={5}
            max={1440}
            value={timeout}
            onChange={(val) => val && setTimeout(val)}
            inputMode="numeric"
            style={{ flex: 1, borderRadius: 12 }}
          />
          <div style={{ background: '#f5f3ff', borderRadius: 10, padding: '8px 14px', flexShrink: 0 }}>
            <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed' }}>{t('common:units.minutesLabel')}</Typography.Text>
          </div>
        </div>

        {/* Quick presets */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[15, 30, 60, 120].map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => setTimeout(val)}
              style={{ flex: 1, height: 34, borderRadius: 10, border: timeout === val ? '2px solid #7c3aed' : '1px solid var(--bl-divider, #ebebeb)', background: timeout === val ? '#f5f3ff' : 'var(--bl-surface, #fff)', color: timeout === val ? '#7c3aed' : 'var(--bl-text-muted, #6b7280)', fontSize: 12, fontWeight: timeout === val ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent' }}
            >
              {t('common:units.minutesShort', { count: val })}
            </button>
          ))}
        </div>

        {hasChanges && (
          <button type="button" onClick={() => void handleSave()} disabled={saving} style={{ width: '100%', height: 46, borderRadius: 12, background: saving ? '#f5f3ff' : '#7c3aed', border: 'none', color: saving ? '#7c3aed' : '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1 }}>
            <SaveOutlined /> {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        )}
      </div>
    </div>
  );
}
